import React, { useRef, useState, useEffect } from 'react';
import { Table, Space, Tag, Progress, Modal, Divider, Button, Avatar, Input, Popover, DatePicker, Empty, Checkbox, Badge } from 'antd';
import { EditTwoTone, DeleteTwoTone, FlagFilled, ExclamationCircleFilled, ClockCircleOutlined, CrownFilled, PlusOutlined, SearchOutlined, CalendarOutlined, BookOutlined, UserOutlined, TeamOutlined, ClearOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import DetailCard from './DetailCard';
import TaskCard from './TaskCard';
import dayjs from 'dayjs';
import { ADMIN } from '../config/Config.js';
import { useUser } from '../contexts/UserContext';
import { PRIORITY, STATUS } from '../config/Config.js';
import { calculateRemainingTime } from '../services/remainingTimeService';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const Tasks = ({ addTask, users, categories, tasks, onEditTask, deleteTask, sFilter, setNotifications }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const rowRefs = useRef({});
  const { user } = useUser();
  const [statusFilter, setStatusFilter] = useState(sFilter);
  const [categoriesFilter, setCategoriesFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [creatorFilter, setCreatorFilter] = useState([]);
  const [assignedUsersFilter, setAssignedUsersFilter] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchTitle, setSearchTitle] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState(tasks);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  // Mobile Filter Modals State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [showAssignedModal, setShowAssignedModal] = useState(false);
  // Sort State
  const [dateSortOrder, setDateSortOrder] = useState('desc'); // 'desc', 'asc'

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  const filterTasks = (searchTitle, dates) => {
    const filtered = tasks.filter((item) => {
      const itemDate = dayjs(item.task.estimatedCompleteDate);
      const startDate = dates[0] ? dayjs(dates[0]) : null;
      const endDate = dates[1] ? dayjs(dates[1]) : null;
      const isWithinRange = !startDate || !endDate || (itemDate.isSameOrAfter(startDate) && itemDate.isSameOrBefore(endDate));
      return item.task.title.toLowerCase().includes(searchTitle.toLowerCase()) && isWithinRange;
    });
    setFilteredTasks(filtered);
  };

  useEffect(() => {
    filterTasks(searchTitle, dateRange);
    setStatusFilter(sFilter);
  }, [sFilter, tasks]);

  // --- EKLEME: GECİKME KORUMASI ---
  // ngrok üzerinden veriler gelene kadar (dizi değilken) filtrenin çökmesini engeller
  if (!Array.isArray(users) || !Array.isArray(categories) || !Array.isArray(tasks)) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Veriler ngrok üzerinden yükleniyor...</div>;
  }
  // -------------------------------

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
    } else {
      setDateRange([]);
    };
  };

  const handleSearch = (e) => {
    if (e && e.target) {
      const newSearchText = e.target.value;
      setSearchTitle(newSearchText);
      filterTasks(newSearchText, dateRange);
    }
  };

  const handleTitleReset = () => {
    setSearchTitle('');
    filterTasks('', dateRange);
  };

  const handleDateReset = () => {
    setDateRange([]);
    filterTasks(searchTitle, []);
  };

  const handleApply = () => {
    filterTasks(searchTitle, dateRange);
  };

  const handleDeleteTask = (record) => {
    confirm({
      title: 'Görevi silmek istiyor musun?',
      icon: <ExclamationCircleFilled />,
      content: <span>{record.task.title}</span>,
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk() {
        deleteTask(record.task);
      },
      onCancel() {
        console.log('Silme işlemi iptal edildi.');
      },
    });
  };

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([record.task.id]);

      setTimeout(() => {
        const rowElement = rowRefs.current[record.task.id];
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);

    } else {
      setExpandedRowKeys([]);
    }
  };

  const handleCategoryAdd = () => {
    addTask(inputValue);
    setInputValue("");
  };

  const rangePresets = [
    {
      label: 'Son 3 Gün',
      value: [dayjs().add(-3, 'd'), dayjs()],
    },
    {
      label: 'Son 5 Gün',
      value: [dayjs().add(-5, 'd'), dayjs()],
    },
    {
      label: 'Son 7 Gün',
      value: [dayjs().add(-7, 'd'), dayjs()],
    },
    {
      label: 'Son 14 Gün',
      value: [dayjs().add(-14, 'd'), dayjs()],
    },
    {
      label: 'Son 30 Gün',
      value: [dayjs().add(-30, 'd'), dayjs()],
    },
    {
      label: 'Son 60 Gün',
      value: [dayjs().add(-60, 'd'), dayjs()],
    },
    {
      label: 'Son 90 Gün',
      value: [dayjs().add(-90, 'd'), dayjs()],
    },
  ];

  const columns = [
    {
      title: '',
      align: 'left',
      dataIndex: ['creator', 'id'],
      width: 30,
      render: (id) => {
        const user = (users || []).find(u => u.id === id);  // users || [] eklendi
        if (user) {
          return user.role === 1 ? <CrownFilled style={{ color: '#F94A29' }} /> : null;
        }
        return null;
      },
      sorter: (a, b) => {
        const dateFormat = 'YYYY-MM-DD';
        const dateB = dayjs(a.task.updateDate, dateFormat);
        const dateA = dayjs(b.task.updateDate, dateFormat);

        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        return 0;
      }
    },
    {
      title: 'Görev',
      align: 'center',
      width: '20%',
      dataIndex: ['task', 'title'],
      sorter: (a, b) => {
        return a.task.title.localeCompare(b.task.title, 'tr', { sensitivity: 'base' });
      },
      sortDirections: ['ascend', 'descend'],
      render: (item) => {
        return <span style={{ fontWeight: '500' }}>{item}</span>
      },
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Görev Ara"
            value={searchTitle}
            onChange={handleSearch}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Button type={"text"} size="small" style={{ color: '#1677ff' }} onClick={handleTitleReset}>Sıfırla</Button>
        </div>
      ),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) =>
        record.task.title.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: (user.role === ADMIN || user.id === 3 ?
        <>
          <Popover
            trigger="click"
            content={
              <Space.Compact style={{ width: '100%' }}>
                <Input value={inputValue} placeholder="Görev adı girin" onChange={(e) => setInputValue(e.target.value)} />
                <Button disabled={!inputValue} type="primary" style={{ backgroundColor: '#3F72AF' }} onClick={handleCategoryAdd}> Ekle </Button>
              </Space.Compact>}
          >
            <Button style={{ padding: '10px', height: '16px', width: '16px' }} type='text' icon={<PlusOutlined />} />
          </Popover>
          <span style={{ marginLeft: '6px' }}>Kategori</span>
        </> :
        <span>Kategori</span>
      ),
      align: 'center',
      dataIndex: 'categories',
      filters: (categories || []).map(category => ({ // categories || [] eklendi
        text: category.name,
        value: category.id,
      })),
      onFilter: (value, record) => {
        return record.categories.some(cat => cat.id === value);
      },
      filteredValue: categoriesFilter,
      filterSearch: true,
      render: (items) => (
        <Avatar.Group
          shape="circle"
          size="medium"
          max={{
            count: 1,
            style: { fontWeight: '500', color: 'white', backgroundColor: '#3F72AF', borderColor: 'rgba(0,0,0,0)', border: '4px, solid', cursor: 'default' },
          }}
        >
          {items && items.length > 0 ? (
            items.map((item) => (
              <Avatar
                shape="square"
                size="large"
                key={item.id}
                style={{ zIndex: '100', backgroundColor: 'rgba(0,0,0,0)', color: 'black', borderColor: 'rgba(0,0,0,0)', fontSize: '14px', fontWeight: '400', transform: '1' }}
              >
                {item.name.length <= 6 ? (item.name) : (item.name.slice(0, 5) + '..')}
              </Avatar>
            ))
          ) : ''}
        </Avatar.Group>
      ),
    },
    {
      title: 'Tahmini Bitiş Tarihi',
      align: 'center',
      dataIndex: ['task', 'estimatedCompleteDate'],
      render: (date, record) => {
        const dateInfo = calculateRemainingTime(date);
        return record.task.progress !== 100 ? (record.task.status !== 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: dateInfo.color }}>
            <span>{dayjs(date).format('DD.MM.YYYY')}</span>
            <span style={{ fontSize: '10px' }}> {dateInfo.status} <ClockCircleOutlined /> </span>
          </div>) : ("")
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span>{dayjs(date).format('DD.MM.YYYY')}</span>
          </div>
        );
      },
      sorter: (a, b) => {
        const dateFormat = 'YYYY-MM-DD';
        const dateA = dayjs(a.task.estimatedCompleteDate, dateFormat);
        const dateB = dayjs(b.task.estimatedCompleteDate, dateFormat);

        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        return 0;
      },
      defaultSortOrder: 'ascend',
      filterDropdown: () => (
        <div style={{ padding: 8, width: '300px' }}>
          <div>
            <RangePicker
              presets={rangePresets}
              format="DD.MM.YYYYY"
              value={dateRange}
              onChange={handleDateRangeChange}
              style={{ marginBottom: 8 }}
              placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
              showTime={false}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button type="text" size="small" style={{ color: '#1677ff' }} onClick={handleDateReset}>Sıfırla</Button>
            <Button type="primary" size="small" onClick={handleApply}>Tamam</Button>
          </div>
        </div>
      ),
      filterIcon: <CalendarOutlined />,
    },
    {
      title: 'Durum',
      align: 'center',
      dataIndex: ['task', 'status'],
      filters: [
        {
          text: 'Ertelendi',
          value: 1,
        },
        {
          text: 'Beklemede',
          value: 2,
        },
        {
          text: 'İşlemde',
          value: 3,
        },
        {
          text: 'Tamamlandı',
          value: 4,
        },
      ],
      onFilter: (value, record) => record.task.status === value,
      filteredValue: statusFilter,
      render: (i) => {
        let color;

        if (STATUS[i] === "Beklemede") {
          color = '#FFEA20';
        } else if (STATUS[i] === 'Ertelendi') {
          color = '#F94A29';
        } else if (STATUS[i] === 'İşlemde') {
          color = '#008DDA';
        } else if (STATUS[i] === 'Tamamlandı') {
          color = '#88D66C';
        }

        return <Tag color={color}>{STATUS[i]}</Tag>;
      },
    },
    {
      title: 'Öncelik',
      align: 'center',
      dataIndex: ['task', 'priority'],
      filters: [
        {
          text: 'Düşük',
          value: 1,
        },
        {
          text: 'Orta',
          value: 2,
        },
        {
          text: 'Yüksek',
          value: 3,
        },
      ],
      onFilter: (value, record) => record.task.priority === value,
      filteredValue: priorityFilter,
      render: (i) => {
        let color;

        if (PRIORITY[i] === 'Düşük') {
          color = 'warning';
        } else if (PRIORITY[i] === 'Orta') {
          color = 'orange';
        } else if (PRIORITY[i] === 'Yüksek') {
          color = 'volcano';
        }

        return (
          <Tag color={color}>
            <FlagFilled style={{ marginRight: '5px' }} /> {PRIORITY[i]}
          </Tag>
        );
      },
    },
    {
      title: 'İlerleme',
      align: 'center',
      dataIndex: ['task', 'progress'],
      sorter: (a, b) => a.task.progress - b.task.progress,
      render: (text) => (
        <div style={{ display: 'inline-block', width: 45, height: 45 }}>
          <Progress type="circle" percent={text} size={45} />
        </div>
      ),
    },
    {
      title: 'Görev Sahibi',
      align: 'center',
      dataIndex: 'creator',
      filters: (users || []) // users || [] eklendi
        .filter(user => user.isActive)
        .map(user => ({
          text: user.name + " " + user.surname,
          value: user.id,
        })),
      onFilter: (value, record) => record.creator.id === value,
      filteredValue: creatorFilter,
      render: (item) => (
        <Avatar style={{ backgroundColor: '#78bf9b', verticalAlign: 'middle' }} size='large' src={item.url}>{item.name}</Avatar>
      ),
    },
    {
      title: 'Görevli Kişiler',
      align: 'center',
      dataIndex: 'assignedUsers',
      filters: (users || []) // users || [] eklendi
        .filter(user => user.isActive)
        .map(user => ({
          text: user.name + " " + user.surname,
          value: user.id,
        })),
      onFilter: (value, record) => {
        return record.assignedUsers.some(aUser => aUser.id === value);
      },
      filteredValue: assignedUsersFilter,
      render: (items) => (
        <Avatar.Group
          size="large"
          max={{
            count: 2,
            style: { fontWeight: '500', color: '#3F72AF', backgroundColor: '#f2dac8' },
          }}
        >
          {items && items.length > 0 ? (
            items.map((item, index) => (
              <Avatar
                key={item.id}
                style={{ backgroundColor: '#78bf9b' }}
                src={item.url}
              >
                {item.name}
              </Avatar>
            ))
          ) : ''}
        </Avatar.Group>
      ),
    },
    {
      title: 'Aksiyon',
      align: 'center',
      dataIndex: '',
      key: 'x',
      render: (text, record) => {
        const canDelete = ((user.id === record.creator.id) || (user.role === ADMIN));

        return (
          <Space>
            <Button type="text" shape="circle" onClick={() => onEditTask(record)}>
              <EditTwoTone twoToneColor="#3F72AF" />
            </Button>

            <Divider type="vertical" />

            {(canDelete && record.task.progress !== 100) ? (
              <Button type="text" shape="circle" onClick={() => handleDeleteTask(record)}>
                <DeleteTwoTone twoToneColor="#3F72AF" />
              </Button>
            ) : (
              <Button style={{ cursor: 'default', visible: 'none' }} type="link" shape="circle"></Button>
            )}
          </Space>
        );
      },
    },
  ];

  const onChange = (filters, sorter, extra) => {
    setStatusFilter(sorter['task.status'] || []);
    setPriorityFilter(sorter['task.priority'] || []);
    setCreatorFilter(sorter['creator'] || []);
    setAssignedUsersFilter(sorter['assignedUsers'] || []);
    setCategoriesFilter(sorter['categories'] || []);
  };

  const locale = {
    filterTitle: 'Filtre Menüsü',
    filterConfirm: 'Tamam',
    filterReset: 'Sıfırla',
    selectAll: 'Tümünü Seç',
    selectInvert: 'Seçimi Ters Çevir',
    sortTitle: 'Sıralama',
    triggerAsc: 'Artan sırayla sıralamak için tıklayın',
    triggerDesc: 'Azalan sırayla sıralamak için tıklayın',
    cancelSort: 'Sıralamayı iptal etmek için tıklayın',
    emptyText: (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<span>Görev Bulunamadı</span>}
      />
    )
  };

  // Mobile Card View
  if (isMobile) {
    // Apply filters for mobile
    const mobileFilteredTasks = filteredTasks.filter(item => {
      // Status Filter
      const sMatch = (!sFilter || sFilter.length === 0) || sFilter.includes(item.task.status);

      // Category Filter
      const catMatch = (!categoriesFilter || categoriesFilter.length === 0) ||
        item.categories.some(c => categoriesFilter.includes(c.id));

      // Creator Filter
      const creatorMatch = (!creatorFilter || creatorFilter.length === 0) ||
        creatorFilter.includes(item.creator.id);

      // Assigned Filter
      const assignMatch = (!assignedUsersFilter || assignedUsersFilter.length === 0) ||
        item.assignedUsers.some(u => assignedUsersFilter.includes(u.id));

      return sMatch && catMatch && creatorMatch && assignMatch;
    }).sort((a, b) => {
      if (!dateSortOrder) return 0;
      const dateA = dayjs(a.task.estimatedCompleteDate);
      const dateB = dayjs(b.task.estimatedCompleteDate);
      if (dateSortOrder === 'asc') return dateA.diff(dateB);
      return dateB.diff(dateA);
    });

    const hasActiveFilters = categoriesFilter.length > 0 || creatorFilter.length > 0 || assignedUsersFilter.length > 0;

    const resetFilters = () => {
      setCategoriesFilter([]);
      setCreatorFilter([]);
      setAssignedUsersFilter([]);
    };

    const toggleDateSort = () => {
      setDateSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    return (
      <div className="task-card-container" style={{
        overflowY: 'hidden', // Main container hidden, verify inner scroll
        height: `calc(100vh - 120px)`,
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {/* Filter Bar */}
        <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px', flexShrink: 0, justifyContent: 'space-between' }} className="mobile-filter-bar">
          <Badge count={categoriesFilter.length} offset={[-5, 5]} size="small" color="#3F72AF">
            <Button size="small" style={{ fontSize: '10px', padding: '0 7px' }} icon={<BookOutlined style={{ fontSize: '10px' }} />} onClick={() => setShowCategoryModal(true)} shape="round">Kategori</Button>
          </Badge>

          <Button
            size="small"
            style={{ fontSize: '10px', padding: '0 7px', color: dateSortOrder ? '#3F72AF' : undefined, borderColor: dateSortOrder ? '#3F72AF' : undefined }}
            icon={dateSortOrder === 'asc' ? <SortAscendingOutlined style={{ fontSize: '10px' }} /> : dateSortOrder === 'desc' ? <SortDescendingOutlined style={{ fontSize: '10px' }} /> : <CalendarOutlined style={{ fontSize: '10px' }} />}
            onClick={toggleDateSort}
            shape="round"
          >
            <span>Tarih <span style={{ fontSize: '9px' }}>({dateSortOrder === 'asc' ? 'Artan' : 'Azalan'})</span></span>
          </Button>

          <Badge count={creatorFilter.length} offset={[-5, 5]} size="small" color="#3F72AF">
            <Button size="small" style={{ fontSize: '10px', padding: '0 7px' }} icon={<UserOutlined style={{ fontSize: '10px' }} />} onClick={() => setShowCreatorModal(true)} shape="round">Oluşturan</Button>
          </Badge>

          <Badge count={assignedUsersFilter.length} offset={[-5, 5]} size="small" color="#3F72AF">
            <Button size="small" style={{ fontSize: '10px', padding: '0 7px' }} icon={<TeamOutlined style={{ fontSize: '10px' }} />} onClick={() => setShowAssignedModal(true)} shape="round">Görevli</Button>
          </Badge>
        </div>

        {hasActiveFilters && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', paddingBottom: '4px' }}>
            <Button
              size="small"
              danger
              type="primary"
              icon={<ClearOutlined />}
              onClick={resetFilters}
              shape="round"
              style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}
            >
              Filtreleri Sıfırla
            </Button>
          </div>
        )}

        {/* Task List */}
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {mobileFilteredTasks.length > 0 ? (
            mobileFilteredTasks.map((task) => (
              <TaskCard
                key={task.task.id}
                task={task}
                users={users}
                onEdit={onEditTask}
                onDelete={handleDeleteTask}
              />
            ))
          ) : (
            <div className="task-card-empty">
              <div className="task-card-empty-icon">📋</div>
              <div className="task-card-empty-text">Görev Bulunamadı</div>
            </div>
          )}
        </div>

        {/* Modals */}
        <Modal
          title="Kategoriler"
          open={showCategoryModal}
          onCancel={() => setShowCategoryModal(false)}
          footer={[<Button key="ok" type="primary" onClick={() => setShowCategoryModal(false)}>Tamam</Button>]}
        >
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            options={categories.map(c => ({ label: c.name, value: c.id }))}
            value={categoriesFilter}
            onChange={setCategoriesFilter}
          />
        </Modal>

        <Modal
          title="Oluşturan Kişi"
          open={showCreatorModal}
          onCancel={() => setShowCreatorModal(false)}
          footer={[<Button key="ok" type="primary" onClick={() => setShowCreatorModal(false)}>Tamam</Button>]}
        >
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            options={users.filter(u => u.isActive).map(u => ({ label: u.name + ' ' + u.surname, value: u.id }))}
            value={creatorFilter}
            onChange={setCreatorFilter}
          />
        </Modal>

        <Modal
          title="Görevli Kişiler"
          open={showAssignedModal}
          onCancel={() => setShowAssignedModal(false)}
          footer={[<Button key="ok" type="primary" onClick={() => setShowAssignedModal(false)}>Tamam</Button>]}
        >
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            options={users.filter(u => u.isActive).map(u => ({ label: u.name + ' ' + u.surname, value: u.id }))}
            value={assignedUsersFilter}
            onChange={setAssignedUsersFilter}
          />
        </Modal>
      </div>
    );
  }

  // Desktop Table View
  return (
    <Table
      locale={locale}
      sticky={true}
      columns={columns}
      pagination={false}
      scroll={{
        y: window.innerHeight * 0.74,
      }}
      onChange={onChange}
      showSorterTooltip={{
        target: 'sorter-icon',
      }}
      expandable={{
        expandedRowRender: (record) => (
          <div ref={(el) => (rowRefs.current[record.task.id] = el)}>
            <DetailCard
              categories={categories}
              users={users}
              data={record}
              setNotifications={setNotifications}
            />
          </div>
        ),
        expandedRowKeys: expandedRowKeys,
        onExpand: handleExpand,
      }}
      dataSource={filteredTasks}
      rowKey={(record) => record.task.id}
      rowClassName={(record, index) => (index % 2 === 1 ? 'striped-row' : '')}
    />
  );
};

export default Tasks;